import { useEffect, useRef, useState } from "react";
import { X } from "react-feather";
import { Title, Text, TextButton } from "../../../../components/BaseComponents/styles";
import { Line } from "../../../../components/Line/styles";
import { Button } from "../../../../components/Button";
import { Colors } from "../../../../values/colors";
import RiskSurveyService from "../../../../services/RiskSurvey";
import { Anchor, Container, Backdrop, StretchedContainer, Content, Select } from "./styles";

const EMPTY_DRAFT = { businessUnit: "", sector: "", subsector: "" };
const EMPTY_OPTIONS = { businessUnit: [], sector: [], subsector: [] };

const FilterField = ({ label, value, options, disabled, onChange }) => (
  <StretchedContainer>
    <Content>
      <Title size={16}>{label}</Title>
      <Select value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">Todos</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </Select>
    </Content>
  </StretchedContainer>
);

export const SurveyFiltersPanel = ({ surveyId, appliedFilters, onFilter }) => {
  const anchorRef = useRef();
  const [opened, setOpened] = useState(false);
  const [anchor, setAnchor] = useState({ top: 0, right: 16 });
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [options, setOptions] = useState(EMPTY_OPTIONS);
  const [applying, setApplying] = useState(false);

  const openPanel = () => {
    const rect = anchorRef.current.getBoundingClientRect();
    setAnchor({ top: rect.top, right: window.innerWidth - rect.right });
    setDraft({
      businessUnit: appliedFilters?.businessUnit || "",
      sector: appliedFilters?.sector || "",
      subsector: appliedFilters?.subsector || "",
    });
    setOpened(true);
  };

  const closePanel = () => setOpened(false);

  const fetchOptions = async (params) => {
    try {
      const data = await RiskSurveyService.getSurveySegmentFilters(surveyId, params);
      return data?.options || { businessUnit: [], setor: [], subsetor: [] };
    } catch (error) {
      console.error("Failed to load survey segment filters:", error);
      return { businessUnit: [], setor: [], subsetor: [] };
    }
  };

  // Nível 1 - Unidade de Negócio, sempre disponível para a pesquisa.
  useEffect(() => {
    if (!opened) return;
    let active = true;
    fetchOptions({}).then((opts) => {
      if (active) setOptions((prev) => ({ ...prev, businessUnit: opts.businessUnit || [] }));
    });
    return () => { active = false; };
  }, [opened, surveyId]);

  // Nível 2 - Setor, escopado pela Unidade de Negócio já escolhida.
  useEffect(() => {
    if (!opened || !draft.businessUnit) {
      setOptions((prev) => ({ ...prev, sector: [] }));
      return;
    }
    let active = true;
    fetchOptions({ business_unit: draft.businessUnit }).then((opts) => {
      if (active) setOptions((prev) => ({ ...prev, sector: opts.setor || [] }));
    });
    return () => { active = false; };
  }, [opened, surveyId, draft.businessUnit]);

  // Nível 3 - Subsetor, escopado pela Unidade de Negócio + Setor escolhidos.
  useEffect(() => {
    if (!opened || !draft.businessUnit || !draft.sector) {
      setOptions((prev) => ({ ...prev, subsector: [] }));
      return;
    }
    let active = true;
    fetchOptions({ business_unit: draft.businessUnit, sector: draft.sector }).then((opts) => {
      if (active) setOptions((prev) => ({ ...prev, subsector: opts.subsetor || [] }));
    });
    return () => { active = false; };
  }, [opened, surveyId, draft.businessUnit, draft.sector]);

  // Trocar um nível invalida os níveis abaixo dele na hierarquia.
  const handleBusinessUnitChange = (value) => setDraft({ businessUnit: value, sector: "", subsector: "" });
  const handleSectorChange = (value) => setDraft((prev) => ({ ...prev, sector: value, subsector: "" }));
  const handleSubsectorChange = (value) => setDraft((prev) => ({ ...prev, subsector: value }));

  const handleClear = () => setDraft(EMPTY_DRAFT);

  const handleApply = async () => {
    if (applying) return;
    setApplying(true);
    try {
      const params = {};
      if (draft.businessUnit) params.business_unit = draft.businessUnit;
      if (draft.sector) params.sector = draft.sector;
      if (draft.subsector) params.subsector = draft.subsector;

      const data = await RiskSurveyService.getSurveySegmentFilters(surveyId, params);
      onFilter && onFilter({
        businessUnit: data?.businessUnit || null,
        sector: data?.setor || null,
        subsector: data?.subsetor || null,
      });
      closePanel();
    } catch (error) {
      console.error("Failed to apply survey segment filters:", error);
    } finally {
      setApplying(false);
    }
  };

  return (
    <>
      {opened && <Backdrop onClick={closePanel} />}
      <Anchor ref={anchorRef}>
        <Container
          className={opened ? "open" : ""}
          style={opened ? { top: anchor.top, right: anchor.right } : undefined}
          onClick={() => !opened && openPanel()}
        >
          <Line align="space-between" className="top">
            <span className="fake-button">
              <svg className="icon" fill="none" width="20" height="14" viewBox="0 0 20 14" xmlns="http://www.w3.org/2000/svg">
                <path fill={Colors.primary} d="M8.91667 13.5C8.60972 13.5 8.35261 13.396 8.14533 13.188C7.93733 12.9807 7.83333 12.7236 7.83333 12.4167C7.83333 12.1097 7.93733 11.8526 8.14533 11.6453C8.35261 11.4373 8.60972 11.3333 8.91667 11.3333H11.0833C11.3903 11.3333 11.6478 11.4373 11.8558 11.6453C12.063 11.8526 12.1667 12.1097 12.1667 12.4167C12.1667 12.7236 12.063 12.9807 11.8558 13.188C11.6478 13.396 11.3903 13.5 11.0833 13.5H8.91667ZM1.33333 2.66667C1.02639 2.66667 0.769278 2.56303 0.562 2.35575C0.354 2.14775 0.25 1.89028 0.25 1.58333C0.25 1.27639 0.354 1.01892 0.562 0.810917C0.769278 0.603639 1.02639 0.5 1.33333 0.5H18.6667C18.9736 0.5 19.2307 0.603639 19.438 0.810917C19.646 1.01892 19.75 1.27639 19.75 1.58333C19.75 1.89028 19.646 2.14775 19.438 2.35575C19.2307 2.56303 18.9736 2.66667 18.6667 2.66667H1.33333ZM4.58333 8.08333C4.27639 8.08333 4.01892 7.97933 3.81092 7.77133C3.60364 7.56406 3.5 7.30694 3.5 7C3.5 6.69306 3.60364 6.43558 3.81092 6.22758C4.01892 6.02031 4.27639 5.91667 4.58333 5.91667H15.4167C15.7236 5.91667 15.9807 6.02031 16.188 6.22758C16.396 6.43558 16.5 6.69306 16.5 7C16.5 7.30694 16.396 7.56406 16.188 7.77133C15.9807 7.97933 15.7236 8.08333 15.4167 8.08333H4.58333Z" />
              </svg>
              <Text className="title" color={Colors.primary} weight="bold">Filtros</Text>
            </span>

            {opened && <X color={Colors.primaryColor} onClick={closePanel} />}
          </Line>

          {opened && (
            <div style={{ width: "100%" }}>
              <Line wrap="wrap" gap="16" alignItems="space-between" className="filter-body">
                <FilterField
                  label="Unidade de Negócio"
                  value={draft.businessUnit}
                  options={options.businessUnit}
                  onChange={handleBusinessUnitChange}
                />
                <FilterField
                  label="Setor"
                  value={draft.sector}
                  options={options.sector}
                  disabled={!draft.businessUnit}
                  onChange={handleSectorChange}
                />
                <FilterField
                  label="Subsetor"
                  value={draft.subsector}
                  options={options.subsector}
                  disabled={!draft.sector}
                  onChange={handleSubsectorChange}
                />
              </Line>

              <Line style={{ width: "100%", marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #eee" }} gap="20" align="end">
                <TextButton onClick={handleClear}><Text weight="bold">Limpar filtros</Text></TextButton>
                <Button onClick={handleApply} color={Colors.primary}>{applying ? "Filtrando..." : "Filtrar"}</Button>
              </Line>
            </div>
          )}
        </Container>
      </Anchor>
    </>
  );
};
